import { useEffect, useRef } from 'react';

import type { AwsWafConfig } from '../types';
import { useAppDispatch } from '../stores/redux.store';
import { closeAwsCaptchaModal } from './aws-captcha-actions';
import { AWS_CAPTCHA_SCRIPT_ID } from './aws-captcha.constants';

type UseAwsWafCaptchaSdkScriptReturnType = {
  /** The script element for the AWS WAF CAPTCHA SDK, if it has been injected */
  scriptElement: HTMLScriptElement | null;
};

/**
 * Injects the AWS WAF CAPTCHA SDK script once a valid configuration is available.
 *
 * The script is intentionally retained after unmounting or a later feature-gate
 * change; it is a page-level dependency and does not need to be loaded twice.
 *
 * Injection is guarded against by the presence of an existing script element in
 * the DOM (keyed on {@link AWS_CAPTCHA_SCRIPT_ID}) rather than a per-instance
 * ref, so remounting this hook cannot append a duplicate script.
 */
export const useAwsWafCaptchaSdkScript = (
  awsWafConfig: AwsWafConfig | null
): UseAwsWafCaptchaSdkScriptReturnType => {
  const dispatch = useAppDispatch();
  const scriptElementRef = useRef<HTMLScriptElement | null>(null);

  useEffect(() => {
    if (!awsWafConfig) {
      return;
    }

    // Reuse the existing script if one has already been injected (e.g. by a
    // previous mount of this hook), so we never append a duplicate.
    const existingScript = document.getElementById(AWS_CAPTCHA_SCRIPT_ID);
    if (existingScript instanceof HTMLScriptElement) {
      scriptElementRef.current = existingScript;
      return;
    }

    const scriptElement = document.createElement('script');
    scriptElement.id = AWS_CAPTCHA_SCRIPT_ID;
    scriptElement.src = awsWafConfig.integrationUrl;
    scriptElement.defer = true;

    scriptElementRef.current = scriptElement;

    scriptElement.addEventListener('error', () => {
      dispatch(closeAwsCaptchaModal());
    });

    document.head.appendChild(scriptElement);
  }, [awsWafConfig, dispatch]);

  return { scriptElement: scriptElementRef.current };
};
